import React, { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from '../data/navigation';
import styles from './MobileNav.module.css';

interface MobileNavProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const POPOVER_ID = 'mobile-nav';

const MobileNav: React.FC<MobileNavProps> = ({
  activeSection,
  onSectionChange,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const closeMenu = (): void => {
    // Popover API: hidePopover may be absent in test environments (jsdom)
    navRef.current?.hidePopover?.();
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ): void => {
    e.preventDefault();
    closeMenu();

    if (isHomePage) {
      onSectionChange(sectionId);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <>
      {/* Menu Toggle Button — popover behavior is wired natively */}
      <button
        type="button"
        className={styles.menuToggle}
        popoverTarget={POPOVER_ID}
        aria-label="メニュー"
      >
        <span className={styles.hamburger}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Side Menu — top layer / light-dismiss / ESC / focus mgmt はブラウザネイティブ */}
      <nav
        ref={navRef}
        id={POPOVER_ID}
        popover="auto"
        className={styles.mobileNav}
        aria-label="モバイルナビゲーション"
      >
        <div className={styles.navItems}>
          {NAV_ITEMS.map((item) =>
            item.kind === 'route' ? (
              <Link
                key={item.id}
                to={item.to}
                className={`${styles.navItem} ${item.emphasis ? styles.emphasis : ''}`}
                onClick={closeMenu}
              >
                {item.label}
              </Link>
            ) : (
              <Link
                key={item.id}
                to={isHomePage ? `#${item.sectionId}` : `/#${item.sectionId}`}
                className={styles.navItem}
                onClick={(e) => handleNavClick(e, item.sectionId)}
                aria-current={
                  activeSection === item.sectionId ? 'page' : undefined
                }
              >
                {item.label}
              </Link>
            )
          )}
        </div>
      </nav>
    </>
  );
};

export default MobileNav;
